/**
 * Outlier rules side by side: Tukey's fences, the z-score rule, the modified z-score
 * (median and MAD), Grubbs' test and Dixon's Q.
 *
 * Sources
 * - Tukey (1977), *Exploratory Data Analysis*; the 2.2 multiplier is Hoaglin & Iglewicz
 *   (1987), "Fine-tuning some resistant rules for outlier labeling", JASA 82, 1147–1149.
 * - Iglewicz & Hoaglin (1993), *How to Detect and Handle Outliers*: modified z-score
 *   M = 0.6745 (x − median) / MAD, flagged at |M| > 3.5; when MAD = 0 they substitute
 *   1.253314 × MeanAD for MAD ÷ 0.6745.
 * - Grubbs (1969), Technometrics 11, 1–21: G = max|x − mean| / s, with the critical value
 *   built from the t distribution (NIST/SEMATECH e-Handbook of Statistical Methods,
 *   section 1.3.5.17). The p-value is the same Bonferroni-exact form R's
 *   `outliers::grubbs.test` evaluates.
 * - Dixon (1950, 1951); critical values for r10 (Q) at 90%, 95% and 99% confidence are
 *   Rorabacher (1991), Analytical Chemistry 63, 139–146 (two-sided: either end may be the
 *   suspect).
 */

import { type QuantileMethod, quantile, sorted } from "@/lib/stats/descriptive";
import { studentTQuantile, studentTUpper } from "@/lib/stats/distributions";

/** Iglewicz & Hoaglin's constant: the 0.75 quantile of the standard normal, 0.6745. */
export const MODIFIED_Z_FACTOR = 0.6745;
/** Used in place of MAD ÷ 0.6745 when the MAD is zero: √(π/2). */
export const MEAN_AD_FACTOR = 1.253_314;
/** Iglewicz & Hoaglin's recommended cut-off for the modified z-score. */
export const MODIFIED_Z_CUTOFF = 3.5;

function mean(values: readonly number[]): number {
  let total = 0;
  for (const value of values) {
    total += value;
  }
  return total / values.length;
}

function sampleSd(values: readonly number[], center: number): number {
  let total = 0;
  for (const value of values) {
    total += (value - center) ** 2;
  }
  return Math.sqrt(total / (values.length - 1));
}

function medianOf(values: readonly number[]): number {
  return quantile(values, 0.5, "inclusive");
}

/* --------------------------------------------------------------------- Tukey */

export interface TukeyResult {
  q1: number;
  q3: number;
  iqr: number;
  k: number;
  lower: number;
  upper: number;
  /** Fences at 3 × IQR, beyond which a value is conventionally "extreme". */
  outerLower: number;
  outerUpper: number;
  flagged: number[];
  extreme: number[];
}

export function tukeyOutliers(
  values: readonly number[],
  method: QuantileMethod,
  k: number
): TukeyResult {
  const data = sorted(values);
  const q1 = quantile(data, 0.25, method);
  const q3 = quantile(data, 0.75, method);
  const iqr = q3 - q1;
  const lower = q1 - k * iqr;
  const upper = q3 + k * iqr;
  const outerLower = q1 - 3 * iqr;
  const outerUpper = q3 + 3 * iqr;
  return {
    q1,
    q3,
    iqr,
    k,
    lower,
    upper,
    outerLower,
    outerUpper,
    flagged: data.filter((value) => value < lower || value > upper),
    extreme: data.filter((value) => value < outerLower || value > outerUpper),
  };
}

/* ------------------------------------------------------------------- z-score */

export interface ZScoreResult {
  mean: number;
  sd: number;
  cutoff: number;
  scores: number[];
  flagged: number[];
  /** No z-score in n values can exceed (n − 1) ÷ √n (Shiffler, 1988). */
  maxPossible: number;
}

/** z = (x − x̄) ÷ s with the sample SD; `scores` follow the sorted data. */
export function zScoreOutliers(
  values: readonly number[],
  cutoff: number
): ZScoreResult | null {
  const data = sorted(values);
  const n = data.length;
  const m = mean(data);
  const sd = sampleSd(data, m);
  if (n < 2 || sd === 0) {
    return null;
  }
  const scores = data.map((value) => (value - m) / sd);
  return {
    mean: m,
    sd,
    cutoff,
    scores,
    flagged: data.filter((_, index) => Math.abs(scores[index]) > cutoff),
    maxPossible: (n - 1) / Math.sqrt(n),
  };
}

/* -------------------------------------------------------------- modified z */

export interface ModifiedZResult {
  median: number;
  mad: number;
  /** The MAD was zero, so MeanAD stood in (Iglewicz & Hoaglin, 1993). */
  usedMeanAd: boolean;
  scores: number[];
  flagged: number[];
  cutoff: number;
}

export function modifiedZOutliers(
  values: readonly number[],
  cutoff = MODIFIED_Z_CUTOFF
): ModifiedZResult | null {
  const data = sorted(values);
  const med = medianOf(data);
  const mad = medianOf(data.map((value) => Math.abs(value - med)));
  let scale = mad / MODIFIED_Z_FACTOR;
  let usedMeanAd = false;
  if (mad === 0) {
    const m = mean(data);
    const meanAd = mean(data.map((value) => Math.abs(value - m)));
    scale = MEAN_AD_FACTOR * meanAd;
    usedMeanAd = true;
  }
  if (scale === 0) {
    return null;
  }
  const scores = data.map((value) => (value - med) / scale);
  return {
    median: med,
    mad,
    usedMeanAd,
    scores,
    flagged: data.filter((_, index) => Math.abs(scores[index]) > cutoff),
    cutoff,
  };
}

/* -------------------------------------------------------------------- Grubbs */

export type Side = "low" | "high";

export interface GrubbsStep {
  n: number;
  mean: number;
  sd: number;
  suspect: number;
  side: Side;
  g: number;
  /** The t quantile behind the critical value. */
  t: number;
  critical: number;
  p: number;
  significant: boolean;
}

/** The largest G a sample of n values can produce: (n − 1) ÷ √n. */
export function grubbsMaxG(n: number): number {
  return (n - 1) / Math.sqrt(n);
}

/**
 * Two-sided critical value:
 * G_crit = ((n − 1) ÷ √n) × √( t² ÷ (n − 2 + t²) ), t = t(1 − α ÷ (2n), n − 2).
 * One-sided uses α ÷ n.
 */
export function grubbsCritical(
  n: number,
  alpha: number,
  twoSided = true
): { critical: number; t: number } {
  const tail = twoSided ? alpha / (2 * n) : alpha / n;
  const t = studentTQuantile(1 - tail, n - 2);
  const critical = grubbsMaxG(n) * Math.sqrt((t * t) / (n - 2 + t * t));
  return { critical, t };
}

/**
 * The p-value of G: with t² = n(n − 2)G² ÷ ((n − 1)² − nG²), p = n × P(T > t) for a
 * one-sided test and twice that for two-sided, capped at 1 (as R's `grubbs.test` does).
 */
export function grubbsPValue(g: number, n: number, twoSided = true): number {
  const room = (n - 1) ** 2 - n * g * g;
  if (room <= 0) {
    return 0;
  }
  const t = Math.sqrt((n * (n - 2) * g * g) / room);
  const oneSided = n * studentTUpper(t, n - 2);
  return Math.min(1, twoSided ? 2 * oneSided : oneSided);
}

/** One Grubbs test on the most extreme value. Needs n ≥ 3 and some spread. */
export function grubbsTest(
  values: readonly number[],
  alpha: number
): GrubbsStep | null {
  const data = sorted(values);
  const n = data.length;
  if (n < 3) {
    return null;
  }
  const m = mean(data);
  const sd = sampleSd(data, m);
  if (sd === 0) {
    return null;
  }
  const lowDistance = m - data[0];
  const highDistance = data[n - 1] - m;
  const side: Side = highDistance >= lowDistance ? "high" : "low";
  const suspect = side === "high" ? data[n - 1] : data[0];
  const g = Math.max(lowDistance, highDistance) / sd;
  const { critical, t } = grubbsCritical(n, alpha);
  const p = grubbsPValue(g, n);
  return {
    n,
    mean: m,
    sd,
    suspect,
    side,
    g,
    t,
    critical,
    p,
    significant: g > critical,
  };
}

/**
 * Grubbs' test applied repeatedly: remove a significant value and test again.
 * The first step is the ordinary single-outlier test; later steps are less reliable
 * (the test assumes at most one outlier), so the widget labels them as exploratory.
 */
export function grubbsRepeated(
  values: readonly number[],
  alpha: number
): GrubbsStep[] {
  const steps: GrubbsStep[] = [];
  let data = sorted(values);
  while (data.length >= 3) {
    const step = grubbsTest(data, alpha);
    if (step === null) {
      break;
    }
    steps.push(step);
    if (!step.significant) {
      break;
    }
    data = step.side === "high" ? data.slice(0, -1) : data.slice(1);
  }
  return steps;
}

/* --------------------------------------------------------------------- Dixon */

export type DixonLevel = 0.1 | 0.05 | 0.01;

/**
 * Rorabacher (1991), Table: Q critical values for r10 = gap ÷ range, indexed by n
 * (3–10); columns are 90%, 95% and 99% confidence (α = 0.10, 0.05, 0.01).
 */
export const DIXON_Q_CRITICAL: Readonly<
  Record<number, readonly [number, number, number]>
> = {
  3: [0.941, 0.97, 0.994],
  4: [0.765, 0.829, 0.926],
  5: [0.642, 0.71, 0.821],
  6: [0.56, 0.625, 0.74],
  7: [0.507, 0.568, 0.68],
  8: [0.468, 0.526, 0.634],
  9: [0.437, 0.493, 0.598],
  10: [0.412, 0.466, 0.568],
};

export const DIXON_MIN_N = 3;
export const DIXON_MAX_N = 10;

export interface DixonResult {
  n: number;
  side: Side;
  suspect: number;
  neighbour: number;
  gap: number;
  range: number;
  q: number;
  critical: number;
  level: DixonLevel;
  significant: boolean;
}

function levelColumn(level: DixonLevel): 0 | 1 | 2 {
  if (level === 0.1) {
    return 0;
  }
  return level === 0.05 ? 1 : 2;
}

/** Dixon's Q for the more suspect end, or null outside n = 3–10 or with zero range. */
export function dixonQ(
  values: readonly number[],
  level: DixonLevel
): DixonResult | null {
  const data = sorted(values);
  const n = data.length;
  if (n < DIXON_MIN_N || n > DIXON_MAX_N) {
    return null;
  }
  const range = data[n - 1] - data[0];
  if (range === 0) {
    return null;
  }
  const lowGap = data[1] - data[0];
  const highGap = data[n - 1] - data[n - 2];
  const side: Side = highGap >= lowGap ? "high" : "low";
  const gap = Math.max(lowGap, highGap);
  const q = gap / range;
  const critical = DIXON_Q_CRITICAL[n][levelColumn(level)];
  return {
    n,
    side,
    suspect: side === "high" ? data[n - 1] : data[0],
    neighbour: side === "high" ? data[n - 2] : data[1],
    gap,
    range,
    q,
    critical,
    level,
    significant: q > critical,
  };
}

/* ------------------------------------------------------------------- Report */

export interface OutlierOptions {
  quartileMethod: QuantileMethod;
  tukeyK: number;
  zCutoff: number;
  alpha: DixonLevel;
  repeatGrubbs: boolean;
}

export interface OutlierReport {
  n: number;
  data: number[];
  tukey: TukeyResult;
  z: ZScoreResult | null;
  modified: ModifiedZResult | null;
  grubbs: GrubbsStep[];
  grubbsFlagged: number[];
  dixon: DixonResult | null;
  dixonFlagged: number[];
}

export interface FlagRow {
  value: number;
  /** How many times the value occurs in the data. */
  count: number;
  tukey: boolean;
  z: boolean;
  modified: boolean;
  grubbs: boolean;
  dixon: boolean;
  /** Number of methods that flag it. */
  votes: number;
}

export function outlierReport(
  values: readonly number[],
  options: OutlierOptions
): OutlierReport {
  const data = sorted(values);
  const grubbs = options.repeatGrubbs
    ? grubbsRepeated(data, options.alpha)
    : [grubbsTest(data, options.alpha)].filter(
        (step): step is GrubbsStep => step !== null
      );
  const grubbsFlagged = grubbs
    .filter((step) => step.significant)
    .map((step) => step.suspect);
  const dixon = dixonQ(data, options.alpha);
  return {
    n: data.length,
    data,
    tukey: tukeyOutliers(data, options.quartileMethod, options.tukeyK),
    z: zScoreOutliers(data, options.zCutoff),
    modified: modifiedZOutliers(data),
    grubbs,
    grubbsFlagged,
    dixon,
    dixonFlagged: dixon?.significant ? [dixon.suspect] : [],
  };
}

/** One row per distinct value that at least one method flags, most votes first. */
export function flagRows(report: OutlierReport): FlagRow[] {
  const flagged = new Set<number>([
    ...report.tukey.flagged,
    ...(report.z?.flagged ?? []),
    ...(report.modified?.flagged ?? []),
    ...report.grubbsFlagged,
    ...report.dixonFlagged,
  ]);
  const rows = [...flagged].map((value) => {
    const tukey = report.tukey.flagged.includes(value);
    const z = report.z?.flagged.includes(value) ?? false;
    const modified = report.modified?.flagged.includes(value) ?? false;
    const grubbs = report.grubbsFlagged.includes(value);
    const dixon = report.dixonFlagged.includes(value);
    return {
      value,
      count: report.data.filter((each) => each === value).length,
      tukey,
      z,
      modified,
      grubbs,
      dixon,
      votes: [tukey, z, modified, grubbs, dixon].filter(Boolean).length,
    };
  });
  return rows.sort((a, b) => b.votes - a.votes || a.value - b.value);
}
